class VariantQuickAdd extends ModalDialog {
  connectedCallback() {
    super.connectedCallback();
    this.bindContent();

    // Close once our product was added; the cart drawer opening (or
    // re-rendering, when already open) with the item is the confirmation.
    this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'product-form') return;
      if (!this.variantData[event.productVariantId]) return;
      this.hide();
    });
  }

  disconnectedCallback() {
    this.unsubscribeCartUpdate?.();
  }

  // Re-queries all content-dependent references. Called once at connect, and
  // again after every fetch-swap (see loadProduct) since that destroys and
  // recreates everything inside [data-quick-add-body].
  bindContent() {
    this.bodyEl = this.querySelector('[data-quick-add-body]');
    const dataEl = this.querySelector('[data-variant-data]');
    this.variantData = dataEl ? JSON.parse(dataEl.textContent) : {};
    this.pillInputs = Array.from(this.querySelectorAll('input[type="radio"]'));
    this.idInput = this.querySelector('[data-quick-add-id-input]');
    this.submitButton = this.querySelector('[data-quick-add-submit]');
    this.imageEl = this.querySelector('[data-quick-add-image]');
    this.priceEl = this.querySelector('[data-quick-add-price]');

    this.pillInputs.forEach((input) => input.addEventListener('change', this.onOptionChange.bind(this)));
  }

  show(opener) {
    const productUrl = opener?.dataset.productUrl;
    if (productUrl && productUrl !== this.loadedProductUrl) {
      this.loadProduct(productUrl, opener);
      return;
    }

    document.querySelectorAll('variant-quick-add[open]').forEach((popup) => {
      if (popup !== this) popup.hide();
    });
    super.show(opener);
  }

  // Fetches a product's quick-add content (sections/quick-add-fragment.liquid)
  // into this (shared) instance's [data-quick-add-body] placeholder, then
  // opens once populated. Used only when the opener carries data-product-url
  // (card-product.liquid, cart-drawer-product-card.liquid) — a self-contained
  // instance's opener never has that attribute, so its show() never reaches here.
  loadProduct(productUrl, opener) {
    const spinner = opener.querySelector('.loading__spinner');
    opener.setAttribute('aria-disabled', 'true');
    opener.classList.add('loading');
    spinner?.classList.remove('hidden');

    // productUrl can already carry a query string (e.g. Shopify's pr_* tracking
    // params on recommendation-sourced URLs in related-products.liquid) — build
    // with URL/searchParams rather than string concatenation so section_id is
    // always a real query param instead of silently corrupting the URL.
    const fetchUrl = new URL(productUrl, window.location.origin);
    fetchUrl.searchParams.set('section_id', 'quick-add-fragment');

    fetch(fetchUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Quick add fragment request failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const freshBody = doc.querySelector('[data-quick-add-body]');
        if (!freshBody || !this.bodyEl) throw new Error('Quick add fragment missing expected content');

        this.bodyEl.outerHTML = freshBody.outerHTML;
        this.bindContent();
        this.loadedProductUrl = productUrl;
        this.openLoaded(opener);
      })
      .catch((error) => {
        console.error(error);
        this.showLoadError(productUrl);
        this.openLoaded(opener);
      })
      .finally(() => {
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        spinner?.classList.add('hidden');
      });
  }

  openLoaded(opener) {
    document.querySelectorAll('variant-quick-add[open]').forEach((popup) => {
      if (popup !== this) popup.hide();
    });
    super.show(opener);
  }

  showLoadError(productUrl) {
    if (!this.bodyEl) return;
    this.loadedProductUrl = null; // allow retry on next click
    this.bodyEl.innerHTML = `
      <div class="variant-quick-add__error" role="alert">
        <p>${window.quickAddStrings?.loadError ?? 'There was an error loading this product. Please try again.'}</p>
        <a href="${productUrl}" class="button button--secondary">${window.quickAddStrings?.viewProduct ?? 'View full details'}</a>
      </div>
    `;
    this.bindContent();
  }

  onOptionChange(event) {
    const legend = event.target.closest('fieldset')?.querySelector('[data-selected-value]');
    if (legend) legend.textContent = event.target.value;

    const selected = {};
    this.pillInputs.forEach((input) => {
      if (input.checked) selected[input.dataset.optionPosition] = input.value;
    });

    const positions = Object.keys(selected).sort((a, b) => a - b);
    const selectedValues = positions.map((position) => selected[position]);

    const match = Object.entries(this.variantData).find(([, variant]) =>
      selectedValues.every((value, index) => variant.options[index] === value)
    );

    if (match) {
      const [variantId, variant] = match;
      this.idInput.value = variantId;
      this.submitButton.disabled = !variant.available;
      this.updatePrice(variant);
      this.updateImage(variant);
    } else {
      this.submitButton.disabled = true;
    }
  }

  updatePrice(variant) {
    let html = `<span class="cart-drawer__price-current">${variant.priceHtml}</span>`;
    if (variant.comparePriceHtml) {
      html += `<s class="cart-drawer__price-compare">${variant.comparePriceHtml}</s>`;
    }
    this.priceEl.innerHTML = html;
  }

  updateImage(variant) {
    if (!variant.image || !this.imageEl) return;
    this.imageEl.src = variant.image;
    this.imageEl.alt = variant.imageAlt;
  }
}

customElements.define('variant-quick-add', VariantQuickAdd);
